import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Card,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Button,
} from "@mui/material";

// Icons
import PrintIcon from "@mui/icons-material/Print";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import EditIcon from "@mui/icons-material/Edit";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";

import PageContainer from "../layouts/PageContainer";
import AppCircularProgress from "../components/common/AppCircularProgress";
import FiscalIntegrationModal from "../components/settings/FiscalIntegrationModal";
import PaymentIntegrationModal from "../components/settings/PaymentIntegrationModal";

// Actions
import {
  getPaymentIntegration,
  getFiscalIntegration,
  resetSettingsServerStatus,
} from "../redux/actions/settingsActions";
import { toast } from "react-toastify";

const SettingsPage = () => {
  const dispatch = useDispatch();

  // --- Redux State ---
  const { userInfo } = useSelector((state) => state.user);
  const {
    paymentIntegration,
    loadingPayment,
    loadingUpdatePayment,
    fiscalIntegration,
    errorUpdatingPayment,
    errorUpdatingFiscal,
    loadingFiscal,
    loadingUpdateFiscal,
    serverStatus,
  } = useSelector((state) => state.settings);

  // --- Local State ---
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [fiscalModalOpen, setFiscalModalOpen] = useState(false);

  // Fetch Integrations
  useEffect(() => {
    dispatch(getPaymentIntegration());
    dispatch(getFiscalIntegration());
  }, [dispatch]);

  // Handle successful updates
  useEffect(() => {
    if (!serverStatus) return;
    if (serverStatus.status === 200) {
      if (serverStatus.operation === "update-payment")
        setPaymentModalOpen(false);
      if (serverStatus.operation === "update-fiscal") setFiscalModalOpen(false);
      dispatch(resetSettingsServerStatus());
    }
  }, [serverStatus, dispatch]);

  const handlePrinterClick = () => {
    toast.info("Printer integration coming soon!");
  };

  const handleAccountClick = () => {
    toast.info("Account profile management coming soon!");
  };

  const isLoadingInitial =
    (loadingPayment && !paymentIntegration) ||
    (loadingFiscal && !fiscalIntegration);

  if (isLoadingInitial) return <AppCircularProgress />;

  return (
    <PageContainer
      title="System Settings"
      subtitle="Manage your integrations, security keys, and hardware configurations."
      breadcrumbs={[
        { label: "Dashboard", to: "/dashboard" },
        { label: "Settings", to: null },
      ]}
    >
      <Stack spacing={4} sx={{ maxWidth: 800, mx: "auto" }}>
        {/* 1. ACCOUNT PROFILE (Dummy) */}
        <Box>
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            sx={{ px: 1 }}
          >
            User Account
          </Typography>
          <Card sx={{ borderRadius: 2 }}>
            <List disablePadding>
              <ListItem button sx={{ py: 2 }} onClick={handleAccountClick}>
                <ListItemIcon>
                  <AccountCircleIcon color="primary" />
                </ListItemIcon>
                <ListItemText
                  primary={`${userInfo?.firstName} ${userInfo?.lastName}`}
                  secondary={
                    userInfo?.email ||
                    "Manage your personal profile and password"
                  }
                />
                <ChevronRightIcon color="action" />
              </ListItem>
            </List>
          </Card>
        </Box>

        {/* 2. PAYMENT INTEGRATION */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
            px={1}
          >
            <Typography variant="h6" fontWeight="bold">
              Payment Gateway
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setPaymentModalOpen(true)}
            >
              Update
            </Button>
          </Stack>
          <Card sx={{ borderRadius: 2 }}>
            <List disablePadding>
              <ListItem>
                <ListItemText
                  primary="Status"
                  secondary={paymentIntegration?.active ? "Active" : "Disabled"}
                  secondaryTypographyProps={{
                    color: paymentIntegration?.active
                      ? "success.main"
                      : "text.secondary",
                  }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="API Keys Configured"
                  secondary={
                    paymentIntegration?.hasGatewayApiKey ? "Yes" : "No"
                  }
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="M-Pesa STK Push"
                  secondary={
                    paymentIntegration?.allowsMpesaStk ? "Enabled" : "Disabled"
                  }
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="M-Pesa C2B"
                  secondary={
                    paymentIntegration?.allowsMpesaC2b ? "Enabled" : "Disabled"
                  }
                />
              </ListItem>
            </List>
          </Card>
        </Box>

        {/* 3. FISCAL (eTIMS) INTEGRATION */}
        <Box>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            mb={1}
            px={1}
          >
            <Typography variant="h6" fontWeight="bold">
              KRA eTIMS Configuration
            </Typography>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              startIcon={<EditIcon />}
              onClick={() => setFiscalModalOpen(true)}
            >
              Update
            </Button>
          </Stack>
          <Card sx={{ borderRadius: 2 }}>
            <List disablePadding>
              <ListItem>
                <ListItemText
                  primary="Status"
                  secondary={fiscalIntegration?.active ? "Active" : "Disabled"}
                  secondaryTypographyProps={{
                    color: fiscalIntegration?.active
                      ? "success.main"
                      : "text.secondary",
                  }}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Environment"
                  secondary={fiscalIntegration?.environment || "Unknown"}
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Device Token Setup"
                  secondary={
                    fiscalIntegration?.hasDeviceToken ? "Configured" : "Missing"
                  }
                  secondaryTypographyProps={{
                    color: fiscalIntegration?.hasDeviceToken
                      ? "success.main"
                      : "error.main",
                  }}
                />
              </ListItem>
            </List>
          </Card>
        </Box>

        {/* 4. DEVICES & PRINTING (Dummy) */}
        <Box mb={4}>
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            sx={{ px: 1 }}
          >
            Hardware
          </Typography>
          <Card sx={{ borderRadius: 2 }}>
            <List disablePadding>
              <ListItem button sx={{ py: 2 }} onClick={handlePrinterClick}>
                <ListItemIcon>
                  <PrintIcon color="action" />
                </ListItemIcon>
                <ListItemText
                  primary="Printer Setup"
                  secondary="Configure default thermal printers"
                />
                <ChevronRightIcon color="action" />
              </ListItem>
            </List>
          </Card>
        </Box>
      </Stack>

      {/* --- MODALS --- */}
      {paymentModalOpen && (
        <PaymentIntegrationModal
          open={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          config={paymentIntegration}
          loading={loadingUpdatePayment}
          error={errorUpdatingPayment}
        />
      )}

      {fiscalModalOpen && (
        <FiscalIntegrationModal
          open={fiscalModalOpen}
          onClose={() => setFiscalModalOpen(false)}
          config={fiscalIntegration}
          loading={loadingUpdateFiscal}
          error={errorUpdatingFiscal}
        />
      )}
    </PageContainer>
  );
};

export default SettingsPage;
